import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';
import ComparisonRange from '../../config/comparisonRange';
import { useAccountPreferences } from '../../contexts/accountPreferencesContext';
import { formatNumber } from '../../utils/formatters/formatNumber';
import { DateComparisonRanges } from '../GlobalFilterBar/types';
import { container, getArrowStyles, getChipStyles, label as labelSx } from './styles';
import { ChipColor } from '../../lib/styles/helpers';

interface DeltaChipIndicatorProps {
  value?: string | number;
  isImprovement: boolean;
  dataTestId: string;
  comparisonRange?: ComparisonRange | DateComparisonRanges | null;
  comparisonDateRange?: {
    startDate: string | null;
    endDate: string | null;
  } | null;
  hideSign?: boolean;
}

export default function DeltaChipIndicator(props: DeltaChipIndicatorProps) {
  const { comparisonRange, comparisonDateRange, value, isImprovement, dataTestId, hideSign } = props;
  const { t } = useTranslation();
  const { preferences } = useAccountPreferences();

  const isNoChange = Math.abs(Number(value)) < 0.00001;

  const upArrow = '↑';
  const downArrow = '↓';
  const arrow = Number(value) > 0 ? upArrow : downArrow;

  let label: string;
  if (comparisonDateRange?.startDate && comparisonDateRange?.endDate) {
    // Format custom date range label
    const startDate = dayjs(comparisonDateRange.startDate).format('MM/DD/YYYY');
    const endDate = dayjs(comparisonDateRange.endDate).format('MM/DD/YYYY');
    // Show single date format if start and end dates are the same
    if (startDate === endDate) {
      label = `${t('deltaIndicatorChip.comparedTo')} ${startDate}`;
    } else {
      label = `${t('deltaIndicatorChip.comparedTo')} ${startDate} - ${endDate}`;
    }
  } else if (comparisonRange) {
    label = t(`deltaIndicatorChip.${comparisonRange}`);
  } else {
    label = t('deltaIndicatorChip.none');
  }

  let chipColor: ChipColor = isImprovement ? 'success' : 'error';
  if (isNoChange) {
    chipColor = 'default';
  }

  return (
    <Box data-testid={dataTestId} sx={container}>
      {comparisonRange || comparisonDateRange ? (
        <Chip
          size='small'
          color={chipColor}
          sx={getChipStyles(isImprovement, isNoChange)}
          label={
            <>
              {!isNoChange && <span style={getArrowStyles(isImprovement)}>{arrow}</span>}
              <span>{`${formatNumber(
                hideSign ? Math.abs(Number(value)) * 100 : Number(value) * 100,
                preferences ?? undefined,
                undefined,
                1
              )}%`}</span>
            </>
          }
        />
      ) : null}
      <Typography color='secondary' data-testid={`${dataTestId}-label`} sx={labelSx}>
        {label}
      </Typography>
    </Box>
  );
}

DeltaChipIndicator.defaultProps = {
  value: undefined,
  comparisonRange: undefined,
  comparisonDateRange: undefined,
  hideSign: false,
};
